// Időkapu-foglaló · Part 3 — Forwarder booking flow.
//
// Wizard-style page at /dock-scheduling/book:
//   1. Pick plant       (dropdown of enabled plants)
//   2. Pick date + dock (native date input + dock select)
//   3. Pick slot        (grid of available slot chips — taken/too_soon greyed)
//   4. Fill form fields (respects field_defs from Part 1; required marked *)
//   5. Submit           → POST /api/dock-scheduling/bookings
//
// Every input has a ds-book-* data-testid.

import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Building2, MapPin, DoorOpen, Clock3, ChevronLeft, Send, Check, ArrowLeft, ListChecks, Printer, CheckCircle2, Ticket, Repeat, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import SlotTicket, { PrintButton } from "./DockSlotTicket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─── Queries ────────────────────────────────────────────────────────────────

const usePlants = () => useQuery({
  queryKey: ["ds-book-plants"],
  queryFn: () => axios.get(`${API}/plants`).then(r => r.data.items || []),
});
const usePlantMeta = (plantId) => useQuery({
  queryKey: ["ds-book-plant-meta", plantId],
  queryFn: () => axios.get(`${API}/plants/${plantId}/meta`).then(r => r.data),
  enabled: !!plantId,
});
const useSlots = (plantId, dockId, date) => useQuery({
  queryKey: ["ds-book-slots", plantId, dockId, date],
  queryFn: () => axios.get(`${API}/bookings/available-slots`, { params: { plant_tenant_id: plantId, dock_id: dockId, date } }).then(r => r.data),
  enabled: !!(plantId && dockId && date),
});

// ─── Field form ─────────────────────────────────────────────────────────────

function FieldInput({ def, value, onChange }) {
  const common = {
    "data-testid": `ds-book-field-${def.field_key}`,
    className: "px-2 py-1.5 rounded w-full",
    style: { background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text, #f0f2f8)", ...mono(12) },
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
  };
  if (def.type === "number") return <input {...common} type="number" />;
  if (def.type === "date") return <input {...common} type="date" />;
  return <input {...common} placeholder={def.type === "plate" ? "pl. ABC-123" : ""} />;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function DockBook() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  const { data: plants = [] } = usePlants();
  const [plantId, setPlantId] = useState("");
  const [date, setDate] = useState(isoToday());
  const [dockId, setDockId] = useState("");
  const [direction, setDirection] = useState("inbound");
  const [gateId, setGateId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [values, setValues] = useState({});

  // Iter145 · B2 — accept `?plant=…&date=…` from the calendar fill-bar deep-link
  // and preselect on first load. Only applies before the user has manually
  // picked something.
  useEffect(() => {
    const p = searchParams.get("plant");
    const d = searchParams.get("date");
    if (p && !plantId) setPlantId(p);
    if (d && d.match(/^\d{4}-\d{2}-\d{2}$/)) setDate(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: meta } = usePlantMeta(plantId);
  const { data: slots, isFetching: slotsFetching } = useSlots(plantId, dockId, date);

  // Reset downstream state when the plant changes.
  useEffect(() => {
    setDockId("");
    setSelectedSlot(null);
    setValues({});
    setGateId("");
  }, [plantId]);

  // Reset slot when dock/date changes.
  useEffect(() => { setSelectedSlot(null); }, [dockId, date]);

  const activeDocks = useMemo(() => (meta?.docks || []).filter(d => {
    if (d.direction === "both") return true;
    return d.direction === direction;
  }), [meta, direction]);

  const requiredKeys = useMemo(() => (meta?.field_defs || []).filter(f => f.is_required).map(f => f.field_key), [meta]);
  const missingRequired = requiredKeys.filter(k => !values[k] || String(values[k]).trim() === "");

  const [confirmed, setConfirmed] = useState(null);  // iter146 · post-book confirmation payload
  const [seriesResult, setSeriesResult] = useState(null);  // iter147 · series creation result

  // iter147 · recurring booking pattern state
  const [recurring, setRecurring] = useState(false);
  const [pattern, setPattern] = useState({
    type: "weekdays",     // "daily" | "weekdays" | "weekly"
    weekdays: [1, 2, 3, 4, 5],  // Mon..Fri default (0=Mon)
    endKind: "count",     // "count" | "date"
    count: 5,
    endDate: "",
  });

  const create = useMutation({
    mutationFn: (body) => axios.post(`${API}/bookings`, body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ds-book-slots"] });
      qc.invalidateQueries({ queryKey: ["ds-my-bookings"] });
      toast.success("Foglalás rögzítve");
      setConfirmed(data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba a foglalás rögzítésekor"),
  });

  const createSeries = useMutation({
    mutationFn: (body) => axios.post(`${API}/bookings/series`, body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ds-book-slots"] });
      qc.invalidateQueries({ queryKey: ["ds-my-bookings"] });
      toast.success(`Sorozat rögzítve — ${data.created_count} időpont`);
      setSeriesResult(data);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.message) {
        setSeriesResult({ created: [], skipped: d.skipped || [], created_count: 0, skipped_count: (d.skipped || []).length, cap_error: d.cap_error, error_message: d.message });
        toast.error(d.message);
      } else {
        toast.error(d || "Hiba a sorozat rögzítésekor");
      }
    },
  });

  const canSubmit = plantId && dockId && selectedSlot && missingRequired.length === 0;

  const submit = () => {
    if (!canSubmit) return;
    if (recurring) {
      const patternPayload = {
        type: pattern.type,
        end: pattern.endKind === "count"
          ? { kind: "count", count: Number(pattern.count) || 1 }
          : { kind: "date", date: pattern.endDate },
      };
      if (pattern.type === "weekdays") {
        patternPayload.weekdays = pattern.weekdays;
      }
      createSeries.mutate({
        plant_tenant_id: plantId,
        dock_id: dockId,
        gate_id: gateId || null,
        slot_start: selectedSlot.slot_start_utc,
        direction,
        field_values: values,
        pattern: patternPayload,
      });
    } else {
      create.mutate({
        plant_tenant_id: plantId,
        dock_id: dockId,
        gate_id: gateId || null,
        slot_start: selectedSlot.slot_start_utc,
        direction,
        field_values: values,
      });
    }
  };

  const cfg = meta?.slot_config;

  // iter147 · Series-result confirmation view
  if (seriesResult) {
    const plantName = (plants.find(p => p.id === plantId) || {}).name || "";
    return (
      <div data-testid="ds-recurring-summary" className="p-1">
        <header className="mb-4">
          <div className="inline-flex items-center gap-2 rounded px-3 py-1.5 mb-3"
            style={{ background: seriesResult.created_count > 0 ? "rgba(34,197,94,0.10)" : "rgba(248,113,113,0.10)",
                     border: `1px solid ${seriesResult.created_count > 0 ? "rgba(34,197,94,0.35)" : "rgba(248,113,113,0.35)"}` }}
          >
            {seriesResult.created_count > 0 ? <CheckCircle2 size={16} color="#22c55e" /> : <AlertTriangle size={16} color="#f87171" />}
            <span style={{ ...mono(11, seriesResult.created_count > 0 ? "#22c55e" : "#f87171", 700, "0.4px") }}>
              {seriesResult.created_count > 0
                ? `SOROZAT RÖGZÍTVE · ${seriesResult.created_count} időpont`
                : "EGY IDŐPONT SEM HOZHATÓ LÉTRE"}
            </span>
          </div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
            <Repeat size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
            Ismétlődő foglalás — összegzés
          </h1>
          {plantName && (
            <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
              {plantName} · {seriesResult.skipped_count} kihagyva
            </div>
          )}
          {seriesResult.cap_error && (
            <div className="mt-2 rounded p-2" style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.40)", ...mono(11, "#f3b14a") }}>
              <AlertTriangle size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
              {seriesResult.cap_error}
            </div>
          )}
        </header>

        {seriesResult.created_count > 0 && (
          <section className="mb-4 rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}>
            <div style={syne(13, 700)} className="mb-2">Létrehozva · {seriesResult.created_count} időpont</div>
            <ul>
              {seriesResult.created.map(b => (
                <li key={b.id} className="py-1 flex items-baseline gap-3" style={{ borderTop: "1px dashed rgba(148,163,184,0.20)" }}>
                  <span style={mono(11, BLUE, 800, "0.6px")}>{b.reference}</span>
                  <span style={mono(12, undefined, 700)}>{(b.slot_start || "").slice(0, 10)}</span>
                  <span style={mono(11, BLUE, 700, "0.4px")}>{b.slot_start_local}–{b.slot_end_local}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {seriesResult.skipped_count > 0 && (
          <section className="mb-4 rounded-md p-3" style={{ background: "rgba(243,177,74,0.08)", border: "1px solid rgba(243,177,74,0.30)" }}>
            <div style={syne(13, 700)} className="mb-2">Kihagyva · {seriesResult.skipped_count} időpont</div>
            <ul>
              {seriesResult.skipped.map((s, i) => (
                <li key={i} className="py-1 flex items-baseline gap-3" style={{ borderTop: i > 0 ? "1px dashed rgba(148,163,184,0.20)" : "none" }}>
                  <span style={mono(12, undefined, 700)}>{s.slot_start_local}</span>
                  <span style={mono(11, "#f3b14a")}>{s.reason}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-4 flex items-center gap-2 justify-center flex-wrap">
          <button
            type="button"
            data-testid="ds-series-goto-mybookings"
            onClick={() => nav("/dock-scheduling/my-bookings")}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
          >
            <ListChecks size={14} /> Foglalásaim
          </button>
          <button
            type="button"
            onClick={() => {
              setSeriesResult(null);
              setSelectedSlot(null);
              setValues({});
            }}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
          >
            <Send size={14} /> Új foglalás
          </button>
        </div>
      </div>
    );
  }

  // iter146 · post-book confirmation view
  if (confirmed) {
    const plantName = (plants.find(p => p.id === (confirmed.tenant_id || plantId)) || {}).name || "";
    const dockName = (meta?.docks || []).find(d => d.id === confirmed.dock_id)?.name || "";
    const gateCode = (meta?.gates || []).find(g => g.id === confirmed.gate_id)?.code || "";
    const enriched = { ...confirmed, dock_name: dockName, gate_code: gateCode };
    return (
      <div data-testid="ds-booking-confirm" className="p-1">
        <header className="mb-4">
          <div className="inline-flex items-center gap-2 rounded px-3 py-1.5 mb-3"
            style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.35)" }}
          >
            <CheckCircle2 size={16} color="#22c55e" />
            <span style={{ ...mono(11, "#22c55e", 700, "0.4px") }}>FOGLALÁS RÖGZÍTVE</span>
          </div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
            <Ticket size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
            Slot-jegy
          </h1>
          <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
            Referencia: <b data-testid="ds-booking-ref" style={{ color: BLUE }}>{confirmed.reference}</b>
            {" · "}
            Mutasd be a portánál — a kapuszolgálat a QR kódot vagy a referenciát olvassa be.
          </div>
        </header>

        <SlotTicket booking={enriched} plantName={plantName} />

        <div className="mt-4 flex items-center gap-2 justify-center flex-wrap">
          <PrintButton />
          <button
            type="button"
            data-testid="ds-booking-goto-mybookings"
            onClick={() => nav("/dock-scheduling/my-bookings")}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
          >
            <ListChecks size={14} /> Foglalásaim
          </button>
          <button
            type="button"
            data-testid="ds-booking-new-again"
            onClick={() => {
              setConfirmed(null);
              setSelectedSlot(null);
              setValues({});
            }}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
          >
            <Send size={14} /> Új foglalás
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ds-book" className="p-1">
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Link to="/dock-scheduling/my-bookings" data-testid="ds-book-back-to-mine"
            className="inline-flex items-center gap-1 px-2 py-1 rounded"
            style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.25)", ...mono(10, "var(--dimmer, #9198b0)", 700, "0.4px"), textDecoration: "none" }}
          ><ArrowLeft size={12} /> Foglalásaim</Link>
        </div>
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <CalendarClock size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Új időkapu-foglalás
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Fuvarozói foglalás plant időzónában · szabad idősávok jelölve
        </div>
      </header>

      {/* Step 1 · Plant */}
      <section className="mb-3 rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Building2 size={14} color={BLUE} />
          <div style={syne(13, 700)}>1. Gyár kiválasztása</div>
        </div>
        <select
          value={plantId}
          onChange={(e) => setPlantId(e.target.value)}
          data-testid="ds-book-plant-select"
          className="px-2 py-1.5 rounded"
          style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12), minWidth: 320 }}
        >
          <option value="">— Válassz gyárat —</option>
          {plants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {plants.length === 0 && (
          <div
            data-testid="ds-book-no-plants"
            className="mt-2 rounded p-2"
            style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.30)", ...mono(11, "#f3b14a") }}
          >
            Még nincs gyár hozzárendelve. Kérj hozzáférést a platform adminisztrátortól.
          </div>
        )}
        {plantId && cfg && (
          <div className="mt-2 flex flex-wrap gap-3" style={mono(10, "var(--dimmer, #9198b0)")}>
            <span><Clock3 size={11} style={{ display: "inline", verticalAlign: -2 }} /> Slot: {cfg.slot_length_minutes} perc</span>
            <span>Előfoglalás: {cfg.lead_time_hours} óra</span>
            <span>Időzóna: {cfg.timezone}</span>
            <span>Módosítható utólag: {cfg.modifiable_by_forwarder ? "igen" : "nem"}</span>
          </div>
        )}
      </section>

      {plantId && (
        <>
          {/* Step 2 · Date + Dock + Direction */}
          <section className="mb-3 rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={14} color={BLUE} />
              <div style={syne(13, 700)}>2. Dátum · irány · dokk</div>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>DÁTUM</div>
                <input type="date" value={date} min={isoToday()} onChange={(e) => setDate(e.target.value)}
                  data-testid="ds-book-date"
                  className="px-2 py-1.5 rounded mt-1"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                />
              </div>
              <div>
                <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>IRÁNY</div>
                <select value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="ds-book-direction"
                  className="px-2 py-1.5 rounded mt-1"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                >
                  <option value="inbound">Bejövő</option>
                  <option value="outbound">Kimenő</option>
                </select>
              </div>
              <div>
                <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>DOKK</div>
                <select value={dockId} onChange={(e) => setDockId(e.target.value)} data-testid="ds-book-dock-select"
                  className="px-2 py-1.5 rounded mt-1"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12), minWidth: 220 }}
                >
                  <option value="">— Válassz dokkot —</option>
                  {activeDocks.map(d => <option key={d.id} value={d.id}>{d.name} ({d.direction})</option>)}
                </select>
              </div>
              {(meta?.gates || []).length > 0 && (
                <div>
                  <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>BEHAJTÁSI KAPU (opcionális)</div>
                  <select value={gateId} onChange={(e) => setGateId(e.target.value)} data-testid="ds-book-gate-select"
                    className="px-2 py-1.5 rounded mt-1"
                    style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                  >
                    <option value="">— Nincs —</option>
                    {meta.gates.map(g => <option key={g.id} value={g.id}>{g.code}{g.description ? ` — ${g.description}` : ""}</option>)}
                  </select>
                </div>
              )}
            </div>
          </section>

          {/* Step 3 · Slot picker */}
          {dockId && (
            <section className="mb-3 rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Clock3 size={14} color={BLUE} />
                <div style={syne(13, 700)}>3. Szabad idősáv</div>
                {slotsFetching && <span style={mono(9, "var(--dimmer)")}>frissítés…</span>}
              </div>
              {slots?.slots?.length === 0 && (
                <div style={mono(11, "var(--dimmer, #9198b0)")}>Erre a napra nincs fogadási idősáv beállítva.</div>
              )}
              {slots?.slots?.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5" data-testid="ds-book-slot-grid">
                  {slots.slots.map(s => {
                    const isSel = selectedSlot?.slot_start_utc === s.slot_start_utc;
                    const disabled = !s.available;
                    const label = disabled
                      ? (s.taken ? "foglalt" : s.too_soon ? "lead-time" : "—")
                      : "";
                    return (
                      <button
                        key={s.slot_start_utc}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && setSelectedSlot(s)}
                        data-testid={`ds-book-slot-${s.slot_start_local}`}
                        data-available={s.available ? "true" : "false"}
                        data-selected={isSel ? "true" : "false"}
                        title={label}
                        className="rounded px-2 py-1.5"
                        style={{
                          background: isSel ? BLUE : disabled ? "rgba(148,163,184,0.06)" : "var(--bg-input, #0a0b12)",
                          color: isSel ? "#fff" : disabled ? "var(--dimmer, #9198b0)" : "var(--text)",
                          border: `1px solid ${isSel ? BLUE : disabled ? "rgba(148,163,184,0.20)" : "rgba(148,163,184,0.35)"}`,
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.55 : 1,
                          ...mono(11, undefined, 700, "0.4px"),
                        }}
                      >
                        {s.slot_start_local}
                        {disabled && (
                          <div style={{ ...mono(8, "var(--dimmer)", 700, "0.3px"), textTransform: "uppercase" }}>{label}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Step 4 · Field values */}
          {selectedSlot && (
            <section className="mb-3 rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
              <div className="flex items-center gap-2 mb-2">
                <ListChecks size={14} color={BLUE} />
                <div style={syne(13, 700)}>4. Foglalási adatok</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(meta?.field_defs || []).map(def => (
                  <label key={def.field_key} className="block">
                    <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>
                      {def.label_hu.toUpperCase()}{def.is_required && <span style={{ color: "#ef4444" }}> *</span>}
                    </div>
                    <FieldInput def={def} value={values[def.field_key]} onChange={(v) => setValues(s => ({ ...s, [def.field_key]: v }))} />
                  </label>
                ))}
              </div>
              {missingRequired.length > 0 && (
                <div className="mt-2" style={mono(10, "#f3b14a")}>
                  Kitöltendő: {missingRequired.join(", ")}
                </div>
              )}
            </section>
          )}

          {/* Step 5 · Recurring toggle (iter147) */}
          {selectedSlot && (
            <section className="mb-3 rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Repeat size={14} color={BLUE} />
                <div style={syne(13, 700)}>5. Ismétlődő foglalás (opcionális)</div>
                <div className="flex-1" />
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ds-recurring-toggle"
                    checked={recurring}
                    onChange={(e) => setRecurring(e.target.checked)}
                  />
                  <span style={mono(11, "var(--text)", 700, "0.4px")}>{recurring ? "BE" : "KI"}</span>
                </label>
              </div>

              {recurring && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>MINTA</div>
                    <select
                      data-testid="ds-recurring-pattern"
                      value={pattern.type}
                      onChange={(e) => setPattern(p => ({ ...p, type: e.target.value }))}
                      className="px-2 py-1.5 rounded w-full mt-1"
                      style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                    >
                      <option value="daily">Napi</option>
                      <option value="weekdays">Adott munkanapok</option>
                      <option value="weekly">Heti</option>
                    </select>
                  </label>

                  {pattern.type === "weekdays" && (
                    <div data-testid="ds-recurring-weekdays">
                      <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>MUNKANAPOK</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {["H", "K", "Sze", "Cs", "P", "Szo", "V"].map((lbl, i) => {
                          const active = pattern.weekdays.includes(i);
                          return (
                            <button
                              key={i}
                              type="button"
                              data-testid={`ds-recurring-weekday-${i}`}
                              onClick={() => setPattern(p => ({
                                ...p,
                                weekdays: active
                                  ? p.weekdays.filter(w => w !== i)
                                  : [...p.weekdays, i].sort(),
                              }))}
                              className="px-2.5 py-1 rounded"
                              style={{
                                background: active ? BLUE : "var(--bg-input, #0a0b12)",
                                color: active ? "#fff" : "var(--text)",
                                border: `1px solid ${active ? BLUE : "rgba(148,163,184,0.30)"}`,
                                cursor: "pointer",
                                ...mono(11, undefined, 700, "0.4px"),
                                minWidth: 40,
                              }}
                            >{lbl}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <label className="block" data-testid="ds-recurring-end">
                    <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>VÉGE</div>
                    <select
                      value={pattern.endKind}
                      onChange={(e) => setPattern(p => ({ ...p, endKind: e.target.value }))}
                      className="px-2 py-1.5 rounded w-full mt-1"
                      style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                    >
                      <option value="count">N alkalom után</option>
                      <option value="date">Adott dátumig</option>
                    </select>
                  </label>

                  {pattern.endKind === "count" && (
                    <label className="block">
                      <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ALKALMAK SZÁMA</div>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={pattern.count}
                        onChange={(e) => setPattern(p => ({ ...p, count: e.target.value }))}
                        data-testid="ds-recurring-count"
                        className="px-2 py-1.5 rounded w-full mt-1"
                        style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                      />
                    </label>
                  )}
                  {pattern.endKind === "date" && (
                    <label className="block">
                      <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ZÁRÓ DÁTUM</div>
                      <input
                        type="date"
                        value={pattern.endDate}
                        onChange={(e) => setPattern(p => ({ ...p, endDate: e.target.value }))}
                        data-testid="ds-recurring-end-date"
                        min={date}
                        className="px-2 py-1.5 rounded w-full mt-1"
                        style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                      />
                    </label>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Submit */}
          {selectedSlot && (
            <div className="flex items-center gap-2 pt-1">
              <div style={mono(11, "var(--dimmer, #9198b0)")}>
                Kiválasztott idősáv: <b style={{ color: "var(--text)" }}>{selectedSlot.slot_start_local}–{selectedSlot.slot_end_local}</b>
              </div>
              <div className="flex-1" />
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit || create.isPending || createSeries.isPending}
                data-testid="ds-book-submit"
                className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                style={{ background: canSubmit ? BLUE : "rgba(148,163,184,0.15)", color: canSubmit ? "#fff" : "var(--dimmer)", border: 0, cursor: canSubmit ? "pointer" : "not-allowed", ...mono(11, undefined, 700, "0.4px") }}
              >{recurring ? <><Repeat size={14} /> Sorozat rögzítése</> : <><Send size={14} /> Foglalás rögzítése</>}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
